import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    domainEventBus,
    RoiCreatedEvent,
    RoiPropertiesUpdatedEvent,
} from '~/domain/events';
import { isViewOfEnvironment } from '~/engine/tool/tools/contouring-tools/editableView';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import {
    currentSetColorEditState,
    uncommittedPointCoordinatesState,
} from '~/state/atoms/structureSet/roiAtoms';
import { RenderAdapter } from '~/viewportGrid/adapters/types';
import { useChangeVersion } from '~/viewportGrid/adapters/useChangeVersion';
import { planeViewOf } from '../ports/PlaneViewRenderAdapter';
import TilePoiOverlay from './TilePoiOverlay';

interface TilePoiMountProps {
    renderer: RenderAdapter;
}

/**
 * Re-renders the mount on the structure-set-gated ROI rail
 * (create/update/delete/point moves), the same signals the engine's own
 * StructureSetObject listens to.
 */
function useRoiRedrawSignal(view: PlaneView | undefined): void {
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!view) {
            return undefined;
        }
        const structureSetUid = view.environment.structureSet?.sopInstanceUID;
        const onRoiEvent = (
            event: RoiPropertiesUpdatedEvent | RoiCreatedEvent
        ): void => {
            if (event.structureSetUid === structureSetUid) {
                setTick((tick) => tick + 1);
            }
        };
        const subscriptions = [
            domainEventBus.subscribe(
                RoiPropertiesUpdatedEvent.EVENT_TYPE,
                onRoiEvent
            ),
            domainEventBus.subscribe(RoiCreatedEvent.EVENT_TYPE, onRoiEvent),
        ];
        return () => {
            for (const subscription of subscriptions) {
                subscription.unsubscribe();
            }
        };
    }, [view]);
}

/**
 * Mounts the display-only point-of-interest overlay over one grid tile,
 * driven by the tile's engine view (the same fence-legal downcast as
 * TileCrosshairMount). Point ROIs render when they lie on the displayed
 * slice; on a tile of the edit target's Environment the selected point ROI
 * also renders off its slice. The selection is a number local to the edit
 * target's structure set, so a tile of another image never applies it; an
 * open colour edit previews on the marker it edits under the same rule, and
 * so does a point move in progress, at the point its release would place.
 * Re-projects on every cursor move, display-scale change (a zoom, a fit or
 * a resize of the tile) and ROI change. Renders nothing for non-engine
 * renderers (the mock adapters).
 */
export default function TilePoiMount({ renderer }: TilePoiMountProps) {
    useChangeVersion(renderer.getCursorSource());
    useChangeVersion(renderer.getDisplayScaleSource());
    const target = useActiveEditTarget();
    const colorEdit = useRecoilValue(currentSetColorEditState);
    const pointMove = useRecoilValue(uncommittedPointCoordinatesState);
    const view = planeViewOf(renderer);
    useRoiRedrawSignal(view);
    if (!view) {
        return null;
    }
    const structures = view.environment.structureSet?.structures ?? [];
    const ofEditTarget = isViewOfEnvironment(view, target.environment);
    const pending = colorEdit?.pending;
    return (
        <TilePoiOverlay
            view={view}
            structures={structures}
            selectedRoiNumber={ofEditTarget ? target.roiNumber : undefined}
            colorPreview={
                ofEditTarget && colorEdit && pending
                    ? { roiNumber: colorEdit.roiNumber, color: pending }
                    : undefined
            }
            pointPreview={(ofEditTarget && pointMove) || undefined}
        />
    );
}
